import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";

import { COUNTRIES } from "../data/countries";

export default function CountrySelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const selected = value || COUNTRIES[0];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => {
      if (c.name.toLowerCase().includes(q)) return true;
      if (c.code.toLowerCase().startsWith(q)) return true;
      if (c.dial.replace("+", "").startsWith(q.replace("+", ""))) return true;
      if ((c.aliases || []).some((a) => a.includes(q))) return true;
      return false;
    });
  }, [query]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        data-testid="country-select-trigger"
        onClick={() => setOpen((o) => !o)}
        className="h-14 px-3 rounded-2xl bg-white border-2 border-slate-900/10 flex items-center gap-1.5 font-heading font-bold text-slate-800 active:translate-y-0.5 transition shadow-[0_3px_0_rgba(0,0,0,0.06)]"
      >
        <span className="text-lg leading-none" aria-hidden>{selected.flag}</span>
        <span className="text-sm">{selected.dial}</span>
        <ChevronDown size={16} strokeWidth={2.75} className="text-slate-500" />
      </button>

      {open && (
        <div
          data-testid="country-select-dropdown"
          className="absolute z-30 left-0 right-0 sm:right-auto mt-2 w-[min(20rem,90vw)] max-h-72 overflow-hidden rounded-2xl bg-white border-2 border-slate-900/10 shadow-[0_8px_0_rgba(0,0,0,0.08),0_24px_60px_-12px_rgba(0,0,0,0.18)]"
        >
          <div className="p-2 border-b border-slate-900/5 flex items-center gap-2 bg-slate-50">
            <Search size={16} strokeWidth={2.5} className="text-slate-400 ml-1" />
            <input
              ref={inputRef}
              data-testid="country-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country or code"
              className="flex-1 bg-transparent outline-none text-sm font-semibold text-slate-700 placeholder:text-slate-400"
            />
          </div>
          <ul className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-slate-500 font-semibold text-center">No matches</li>
            ) : (
              filtered.map((c) => (
                <li key={c.code}>
                  <button
                    type="button"
                    data-testid={`country-option-${c.code}`}
                    onClick={() => {
                      onChange(c);
                      setQuery("");
                      setOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 hover:bg-pink-50 transition ${
                      selected.code === c.code ? "bg-pink-50" : ""
                    }`}
                  >
                    <span className="text-lg" aria-hidden>{c.flag}</span>
                    <span className="flex-1 text-sm font-bold text-slate-800 truncate">{c.name}</span>
                    <span className="text-xs text-slate-500 font-bold tabular-nums">{c.dial}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
